import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { animate, motion, useMotionValue, useReducedMotion, useTransform } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import type { LibrarySpace } from '@/lib/library/collections';
import { LibraryGrid } from './LibraryGrid';
import { SourceGlyph } from './SourceGlyph';
import { CoverFace, SpaceTile } from './SpaceCover';
import { SPACE_SPRING, countLabel } from './spaceMotion';

export interface SpaceRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type SpaceCloseMode = 'back' | 'swipe';

export interface SpaceScreenProps {
  space: LibrarySpace;
  /** Child boards that hold anything (board spaces only). */
  childSpaces: LibrarySpace[];
  /** The front cover's box in the Spaces container, to grow out of and shrink back into. */
  origin: SpaceRect | null;
  /** Set when the screen should leave; it calls onExited when it has. */
  closing: SpaceCloseMode | null;
  backLabel: string;
  onBack: () => void;
  onSwipeClose: () => void;
  onExited: () => void;
  onOpenChild: (space: LibrarySpace, front: HTMLElement | null) => void;
  zIndex: number;
}

/** Touches that start this close to the left edge can swipe the screen away. */
const EDGE = 20;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * One open space. It grows out of its front cover: the screen is revealed
 * through a rounded window that expands from the cover's box to the full
 * area while a copy of the front card grows with it and fades out, so the
 * cover reads as becoming the space. Closing runs it backwards into the
 * pile. A swipe from the left edge drags the screen off to the right.
 * Reduced motion: a short cross-fade either way.
 *
 * The cards render in LibraryGrid via its `header` prop, so the grid's own
 * gestures and detail view apply here too.
 */
export function SpaceScreen({ space, childSpaces, origin, closing, backLabel, onBack, onSwipeClose, onExited, onOpenChild, zIndex }: SpaceScreenProps) {
  const reduce = useReducedMotion();
  const grow = !!origin && !reduce;
  const rootRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<{ w: number; h: number } | null>(null);
  const originRef = useRef<SpaceRect | null>(origin);
  const progress = useMotionValue(grow ? 0 : 1);
  const fade = useMotionValue(grow ? 1 : 0);
  const x = useMotionValue(0);
  const front = space.covers[0];

  // The window and the flying cover share one rect, from the cover (0) to the full area (1)
  const rectAt = (v: number): SpaceRect | null => {
    const box = boxRef.current;
    const o = originRef.current;
    if (!box || !o) return null;
    const t = Math.min(1, Math.max(0, v));
    return { x: lerp(o.x, 0, t), y: lerp(o.y, 0, t), w: lerp(o.w, box.w, t), h: lerp(o.h, box.h, t) };
  };
  const clipPath = useTransform(progress, (v) => {
    if (v >= 1) return 'none';
    const box = boxRef.current;
    const r = rectAt(v);
    if (!box || !r) return 'inset(0 0 100% 0)';
    const radius = lerp(12, 0, Math.min(1, Math.max(0, v)));
    return `inset(${r.y}px ${box.w - r.x - r.w}px ${box.h - r.y - r.h}px ${r.x}px round ${radius}px)`;
  });
  // One pixel larger than the window on every side, so no anti-aliased seam shows the screen early
  const flyLeft = useTransform(progress, (v) => (rectAt(v)?.x ?? 0) - 1);
  const flyTop = useTransform(progress, (v) => (rectAt(v)?.y ?? 0) - 1);
  const flyWidth = useTransform(progress, (v) => (rectAt(v)?.w ?? 0) + 2);
  const flyHeight = useTransform(progress, (v) => (rectAt(v)?.h ?? 0) + 2);
  const flyRadius = useTransform(progress, [0, 1], [12, 0]);
  const flyOpacity = useTransform(progress, [0, 0.45, 0.85], [1, 1, 0]);

  // Measure, then open
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (el) boxRef.current = { w: el.offsetWidth, h: el.offsetHeight };
    originRef.current = origin;
    if (grow) progress.set(0.0001);
    const controls = grow ? animate(progress, 1, SPACE_SPRING) : animate(fade, 1, { duration: 0.2, ease: [0.22, 1, 0.36, 1] });
    return () => controls.stop();
    // Mount only: the origin is fixed for this screen's life
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Leave. onExited arrives inline; a ref keeps re-renders from restarting the exit.
  const exitedRef = useRef(onExited);
  useEffect(() => {
    exitedRef.current = onExited;
  }, [onExited]);
  useEffect(() => {
    if (!closing) return;
    const width = boxRef.current?.w ?? window.innerWidth;
    const controls =
      closing === 'swipe'
        ? animate(x, width, { ...SPACE_SPRING, restDelta: 0.5 })
        : grow
          ? animate(progress, 0, { ...SPACE_SPRING, restDelta: 0.002 })
          : animate(fade, 0, { duration: 0.18, ease: [0.22, 1, 0.36, 1] });
    let done = false;
    controls.then(() => {
      if (!done) exitedRef.current();
    });
    return () => {
      done = true;
      controls.stop();
    };
  }, [closing, grow, progress, fade, x]);

  // Edge swipe. Native listeners: React's touchmove is passive and cannot hold the page still.
  const swipeCloseRef = useRef(onSwipeClose);
  useEffect(() => {
    swipeCloseRef.current = onSwipeClose;
  }, [onSwipeClose]);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || closing) return;
    let start: { x: number; y: number; t: number } | null = null;
    let swiping = false;
    let last = { x: 0, t: 0 };
    let velocity = 0;

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const touch = e.touches[0];
      const left = el.getBoundingClientRect().left;
      if (touch.clientX - left > EDGE) return;
      start = { x: touch.clientX, y: touch.clientY, t: e.timeStamp };
      last = { x: touch.clientX, t: e.timeStamp };
      swiping = false;
      velocity = 0;
    };
    const onMove = (e: TouchEvent) => {
      if (!start) return;
      const touch = e.touches[0];
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      if (!swiping) {
        if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
          start = null;
          return;
        }
        if (dx > 8 && dx > Math.abs(dy) * 1.2) swiping = true;
        else return;
      }
      e.preventDefault();
      const dt = Math.max(1, e.timeStamp - last.t);
      velocity = (touch.clientX - last.x) / dt;
      last = { x: touch.clientX, t: e.timeStamp };
      x.set(Math.max(0, dx));
    };
    const onEnd = () => {
      if (!start) return;
      const wasSwiping = swiping;
      start = null;
      swiping = false;
      if (!wasSwiping) return;
      const width = el.offsetWidth;
      if (x.get() > width * 0.33 || velocity > 0.5) swipeCloseRef.current();
      else animate(x, 0, SPACE_SPRING);
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [closing, x]);

  const header = (
    <>
      <div className="sticky top-0 z-10 flex h-12 items-center justify-between bg-paper px-2">
        <button
          type="button"
          onClick={onBack}
          aria-label={backLabel}
          className="flex h-10 w-10 items-center justify-center rounded-full text-ink-2 outline-none transition-colors duration-150 active:bg-wash focus-visible:ring-2 focus-visible:ring-signal"
        >
          <ChevronLeft className="h-5 w-5" strokeWidth={1.75} />
        </button>
        <span className="pr-3 font-mono text-[11px] uppercase tracking-[0.08em] text-ink-3">{countLabel(space.cards.length)}</span>
      </div>
      <div className="px-5 pb-4">
        <h2 className="flex min-w-0 items-center gap-2 font-display text-[28px] font-medium leading-[34px] tracking-[-0.02em] text-ink">
          {space.source && (
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] border border-line bg-paper-raised text-ink">
              <SourceGlyph source={space.source} className="h-4 w-4" />
            </span>
          )}
          <span className="truncate">{space.label}</span>
        </h2>
      </div>
      {childSpaces.length > 0 && (
        <div className="pb-5">
          <div className="scrollbar-hide flex gap-3 overflow-x-auto px-4 pb-1 pt-1">
            {childSpaces.map((child) => (
              <SpaceTile key={child.id} space={child} size="sm" onOpen={onOpenChild} className="w-[104px] shrink-0" />
            ))}
          </div>
        </div>
      )}
    </>
  );

  return (
    <motion.div
      ref={rootRef}
      role="region"
      aria-label={space.label}
      className="absolute inset-0 bg-paper"
      style={{ zIndex, clipPath, opacity: fade, x }}
    >
      {/* Hairline on the left edge, seen only while the screen is swiped */}
      <span aria-hidden className="pointer-events-none absolute inset-y-0 -left-px w-px bg-line" />
      <LibraryGrid cards={space.cards} header={header} />
      {grow && front && (
        <motion.div
          aria-hidden
          className="pointer-events-none absolute overflow-hidden border border-line"
          style={{ left: flyLeft, top: flyTop, width: flyWidth, height: flyHeight, borderRadius: flyRadius, opacity: flyOpacity }}
        >
          <CoverFace card={front} fadeIn={false} />
        </motion.div>
      )}
    </motion.div>
  );
}
